import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { num, pct, compact } from "@/lib/format";
import { Table2, AlertTriangle, Bug, X } from "lucide-react";
import { visibleInterval } from "@/lib/poll";
import { useApp } from "@/context/AppContext";

const STATUS_CLS = {
  OK: "bg-[#00C805]/15 text-[#00C805]",
  WAITING_FOR_TICKS: "bg-[#FFCC00]/15 text-[#FFCC00]",
  NO_FUTURES_CONTRACTS: "bg-[#94A3B8]/15 text-slate-300",
  MARKET_DATA_INVALID: "bg-[#FF3B30]/20 text-[#FF6b62]",
};

function Diag({ symbol, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/paper/explorer/${symbol}/diagnostic`).then((r) => setD(r.data)).catch(() => {});
  }, [symbol]);
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose}>
      <div className="w-[520px] max-w-full h-full overflow-auto bg-[#12151C] border-l border-[#2A2F3A] p-5" onClick={(e) => e.stopPropagation()} data-testid="paper-diag-drawer">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-bold flex items-center gap-2"><Bug className="h-4 w-4 text-[#5b9bff]" /> {symbol} · Zerodha Diagnostic</div>
          <button onClick={onClose}><X className="h-4 w-4 text-slate-500" /></button>
        </div>
        {!d ? <div className="text-xs text-slate-500">Loading…</div> : (
          <pre className="text-[11px] mono whitespace-pre-wrap text-slate-300 bg-[#0A0C10] border border-[#2A2F3A] rounded-sm p-3">
{JSON.stringify(d, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

function ageText(sec) {
  if (sec == null) return "—";
  if (sec < 60) return `${Math.max(0, Math.round(sec))}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
  return `${Math.floor(sec / 86400)}d`;
}

export default function PaperExplorer() {
  const [data, setData] = useState(null);
  const [diag, setDiag] = useState(null);
  const nav = useNavigate();
  const { systemStatus } = useApp() || {};
  const nfoOpen = !!systemStatus?.market?.exchanges?.NFO?.open;     // an old last trade is only "stale" in session

  useEffect(() => {
    const load = () => api.get("/paper/explorer").then((r) => setData(r.data)).catch(() => {});
    load();
    return visibleInterval(load, 3000);
  }, []);

  if (!data) return <div className="p-4 text-slate-500">Loading PAPER explorer…</div>;

  if (!data.zerodha_ready) {
    return (
      <div className="p-4">
        <div className="term-panel rounded-sm p-8 text-center" data-testid="paper-explorer-waiting">
          <AlertTriangle className="h-8 w-8 text-[#FFCC00] mx-auto mb-3" />
          <div className="text-sm font-semibold">WAITING FOR ZERODHA</div>
          <div className="text-xs text-slate-500 mt-1">{data.message}</div>
          <div className="text-[11px] text-slate-600 mt-2">PAPER uses live Zerodha data only — there is no synthetic fallback.</div>
          <button onClick={() => nav("/broker")} className="mt-4 text-xs font-semibold px-3 py-1.5 rounded-sm bg-[#2563EB]">Go to Broker Session</button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-3">
      {diag && <Diag symbol={diag} onClose={() => setDiag(null)} />}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-bold tracking-tight flex items-center gap-2"><Table2 className="h-5 w-5 text-[#00C805]" /> Futures Explorer
          <span className="text-xs font-normal ml-2 px-2 py-0.5 rounded-sm bg-[#00C805]/15 text-[#00C805]" data-testid="paper-explorer-source">PAPER · MARKET DATA: ZERODHA</span>
        </h1>
        <div className="text-[11px] text-slate-500 mono">{data.rows.length} underlyings · {data.subscribed_tokens} tokens subscribed · instrument master {compact(data.instrument_count)}</div>
      </div>

      <div className="term-panel rounded-sm overflow-auto" style={{ maxHeight: "calc(100vh - 200px)" }}>
        <table className="w-full text-xs border-collapse">
          <thead className="sticky top-0 z-10 bg-[#1A1D24]">
            <tr className="text-slate-400">
              {["Symbol", "Src", "Spot", "Near", "N.Bid", "N.Ask", "N.OI", "Mid", "Far", "F.Bid", "F.Ask", "F.OI",
                "N→M", "M→F", "N→F", "N→F%", "Struct", "Last Trade", "Status", "Diag"].map((h, i) => (
                <th key={h} className={`px-2 py-2 whitespace-nowrap font-semibold ${i === 0 || i === 1 || i > 15 ? "text-left" : "text-right"}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => {
              // exchange time of the near leg's last trade: arrival age looked "2.4s" at 3 AM
              const nearAge = r.near?.trade_age;
              return (
                <tr key={r.symbol} className="gz-row mono">
                  <td className="px-2 py-1.5 text-left font-bold text-[var(--term-primary)] sticky left-0 bg-[#12151C]">{r.symbol}</td>
                  <td className="px-2 py-1.5 text-left"><span className={`text-[9px] px-1 rounded-sm ${r.data_source === "ZERODHA" ? "bg-[#00C805]/15 text-[#00C805]" : "bg-[#FF3B30]/20 text-[#FF6b62]"}`}>{r.data_source}</span></td>
                  <td className="px-2 py-1.5 text-right">{r.spot != null ? num(r.spot, 2) : "—"}</td>
                  <td className="px-2 py-1.5 text-right text-slate-100">{r.near_price != null ? num(r.near_price, 2) : "—"}</td>
                  <td className="px-2 py-1.5 text-right text-slate-400">{r.near?.bid != null ? num(r.near.bid, 2) : "—"}</td>
                  <td className="px-2 py-1.5 text-right text-slate-400">{r.near?.ask != null ? num(r.near.ask, 2) : "—"}</td>
                  <td className="px-2 py-1.5 text-right text-slate-400">{compact(r.near?.oi)}</td>
                  <td className="px-2 py-1.5 text-right">{r.mid_price != null ? num(r.mid_price, 2) : "—"}</td>
                  <td className="px-2 py-1.5 text-right">{r.far_price != null ? num(r.far_price, 2) : "—"}</td>
                  <td className="px-2 py-1.5 text-right text-slate-400">{r.far?.bid != null ? num(r.far.bid, 2) : "—"}</td>
                  <td className="px-2 py-1.5 text-right text-slate-400">{r.far?.ask != null ? num(r.far.ask, 2) : "—"}</td>
                  <td className="px-2 py-1.5 text-right text-slate-400">{compact(r.far?.oi)}</td>
                  <td className="px-2 py-1.5 text-right">{r.near_mid_gap_abs != null ? num(r.near_mid_gap_abs, 2) : "—"}</td>
                  <td className="px-2 py-1.5 text-right">{r.mid_far_gap_abs != null ? num(r.mid_far_gap_abs, 2) : "—"}</td>
                  <td className="px-2 py-1.5 text-right">{r.near_far_gap_abs != null ? num(r.near_far_gap_abs, 2) : "—"}</td>
                  <td className={`px-2 py-1.5 text-right ${r.near_far_gap_pct > 0 ? "text-[#00C805]" : r.near_far_gap_pct < 0 ? "text-[#FF3B30]" : ""}`}>{r.near_far_gap_pct != null ? pct(r.near_far_gap_pct, 2) : "—"}</td>
                  <td className="px-2 py-1.5 text-left">{r.direction ? <span className={`px-1 rounded-sm text-[9px] font-bold ${r.direction === "contango" ? "bg-[#2563EB]/15 text-[#5b9bff]" : "bg-[#FFCC00]/15 text-[#FFCC00]"}`}>{r.direction === "contango" ? "CONTANGO" : "BACKWDN"}</span> : "—"}</td>
                  <td className={`px-2 py-1.5 text-left ${nfoOpen && nearAge != null && nearAge > 10 ? "text-[#FF6b62]" : "text-slate-500"}`} data-testid={`last-trade-${r.symbol}`}
                    title="How long ago the exchange stamped the near leg's last price">{ageText(nearAge)}</td>
                  <td className="px-2 py-1.5 text-left"><span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold ${STATUS_CLS[r.market_data_status] || "bg-[#1A1D24] text-slate-400"}`}>{r.market_data_status}</span></td>
                  <td className="px-2 py-1.5 text-left"><button onClick={() => setDiag(r.symbol)} data-testid={`paper-diag-${r.symbol}`} className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-[#1A1D24] border border-[#2A2F3A] hover:bg-[#232730]">tokens</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-slate-600">PAPER binds strictly to Zerodha instrument master (NSE spot + NFO futures by expiry) and live ticks. Rows with any non-ZERODHA instrument are flagged MARKET_DATA_INVALID.</div>
    </div>
  );
}
